import axios from "axios";

import env from "../Config/env";
import authStorage from "./authStorage";
import logger from "./logger";
import { decideOn401, isAuthEndpoint } from "./authPolicy";

// Re-export para compatibilidade (consumidores antigos importavam daqui).
export { decideOn401, isAuthEndpoint };

/**
 * Silent-refresh do modelo de 2 tokens (Auth Fase 2).
 *
 * - `refreshAccessToken()`: single-flight — 1 refresh por vez; chamadas
 *   concorrentes reusam a MESMA promise. Usa `axios` cru (não o `api`) para não
 *   recursar no interceptor. Persiste o novo par via `authStorage.setSession`
 *   (o refresh é ROTATIVO: sempre substituir).
 * - `decideOn401(code)`: decisão pura por código de 401 (testável).
 * - `handleLogoff()`: limpa tokens + dispara o callback registrado pelo app.
 *
 * Gating de segurança (antes da virada da Fase B): nada disso desloga/renova se
 * não houver refresh token. Ver uso no interceptor (`Services/api.js`).
 */

let _refreshPromise = null;
let _onLogoff = null;     // app registra: dispatch(logout) + navigate
let _onRefreshed = null;  // app registra: re-auth do socket com novo access

export const setOnLogoff = (fn) => { _onLogoff = fn; };
export const setOnRefreshed = (fn) => { _onRefreshed = fn; };

/**
 * Logout: invalida a sessão no servidor (fire-and-forget) e limpa os tokens locais.
 * Usa axios cru (não o `api`) e só chama /auth/logout se houver refresh token.
 */
export const serverLogout = () => {
	const refreshToken = authStorage.getRefreshToken();
	if (refreshToken) {
		axios.post(`${env.API_URL}/auth/logout`, { refreshToken }, { timeout: 15000 })
			.catch((e) => logger.warn("[authRefresh] /auth/logout falhou (ignorado)", e?.message));
	}
	authStorage.clearAll();
};

export const handleLogoff = () => {
	authStorage.clearAll();
	if (_onLogoff) {
		try { _onLogoff(); return; } catch (e) { logger.error("[authRefresh] onLogoff falhou", e); }
	}
	try { window.location.assign("/"); } catch { /* noop */ }
};

/**
 * Renova o access usando o refresh token (body). Single-flight.
 * @returns {Promise<string>} novo accessToken
 */
export const refreshAccessToken = () => {
	if (_refreshPromise) return _refreshPromise;

	const refreshToken = authStorage.getRefreshToken();
	if (!refreshToken) return Promise.reject(new Error("NO_REFRESH_TOKEN"));

	_refreshPromise = axios
		.post(`${env.API_URL}/auth/refresh`, { refreshToken }, { timeout: 30000 })
		.then((res) => {
			const data = res?.data || {};
			if (!data.accessToken) throw new Error("REFRESH_NO_ACCESS");
			authStorage.setSession({
				accessToken: data.accessToken,
				refreshToken: data.refreshToken,           // rotacionado
				companyStatusToken: data.companyStatusToken,
				activationStatusToken: data.activationStatusToken,
			});
			if (_onRefreshed) {
				try { _onRefreshed(data.accessToken); } catch (e) { logger.error("[authRefresh] onRefreshed falhou", e); }
			}
			return data.accessToken;
		})
		.finally(() => { _refreshPromise = null; });

	return _refreshPromise;
};

/**
 * Refresh proativo (Fase C): renova o access ~5min ANTES de expirar, enquanto a
 * sessão está ativa — evitando até o 401 reativo. Reagenda após cada refresh
 * (o novo access tem novo `exp`). Self-contained: um único timer por vez.
 *
 * O app chama `startProactiveRefresh()` ao logar e `stopProactiveRefresh()` ao
 * deslogar (ver layouts Desktop/Mobile). O refresh reativo no interceptor segue
 * como rede de segurança caso o timer não dispare (aba em background etc.).
 */
let _proactiveTimer = null;
const PROACTIVE_SKEW_MS = 5 * 60 * 1000; // 5 min antes do exp
const PROACTIVE_MIN_MS = 10 * 1000;      // piso p/ não agendar em loop curto

export const stopProactiveRefresh = () => {
	if (_proactiveTimer) { clearTimeout(_proactiveTimer); _proactiveTimer = null; }
};

export const startProactiveRefresh = () => {
	stopProactiveRefresh();
	const schedule = () => {
		const expMs = authStorage.getAccessExpiryMs();
		if (!expMs) return; // sem access decodificável — nada a agendar
		let delay = expMs - Date.now() - PROACTIVE_SKEW_MS;
		if (delay < PROACTIVE_MIN_MS) delay = PROACTIVE_MIN_MS;
		_proactiveTimer = setTimeout(() => {
			if (!authStorage.hasRefreshToken()) return; // sem refresh, não há o que renovar
			refreshAccessToken()
				.then(() => schedule())                 // reagenda com o novo exp
				.catch((e) => logger.warn("[authRefresh] refresh proativo falhou (o 401 reativo cobre)", e?.message));
		}, delay);
	};
	schedule();
};

export default refreshAccessToken;
